import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';

import {FormsModule} from '@angular/forms';
import {firstValueFrom} from 'rxjs';
import {toSignal} from '@angular/core/rxjs-interop';
import {DirectusService, getDirectusTranslation, Module} from '@frontend/directus';
import {ProjectCoverageDataControllerService} from '@frontend/shared-api';
import {TranslocoPipe, TranslocoService} from '@jsverse/transloco';
import {MultiSelectModule} from 'primeng/multiselect';
import {TableExpandControlsComponent} from '../../../../../../../../shared/components/table/table-expand-controls/table-expand-controls.component';
import {LoadingComponent} from '../../../../../../../../shared/components/loading/loading.component';
import {ProviderOverallResult} from '../../types/overall-results.types';

interface ComparisonProvider {
  hrToolDirectusId: string;
  translatedName: string;
  supportedFunctionIds: Set<number>;
}

interface ComparisonFunctionRow {
  functionId: number;
  functionName: string;
  status: 'required' | 'extra';
  supportedByToolIds: Set<string>;
}

type FunctionStatus = ComparisonFunctionRow['status'];
const ALL_FUNCTION_STATUSES: FunctionStatus[] = ['required', 'extra'];
type SupportFilterValue = 'supported' | 'notSupported';
const ALL_SUPPORT_FILTER_VALUES: SupportFilterValue[] = ['supported', 'notSupported'];

interface ComparisonModuleRow {
  moduleId: number;
  moduleName: string;
  functions: ComparisonFunctionRow[];
}

@Component({
  selector: 'app-module-comparison',
  imports: [
    FormsModule,
    TranslocoPipe,
    MultiSelectModule,
    TableExpandControlsComponent,
    LoadingComponent,
  ],
  templateUrl: './module-comparison.component.html',
  styleUrl: './module-comparison.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModuleComparisonComponent {
  private readonly directusService = inject(DirectusService);
  private readonly coverageDataService = inject(ProjectCoverageDataControllerService);
  private readonly translocoService = inject(TranslocoService);
  private readonly activeLanguage = toSignal(this.translocoService.langChanges$, {
    initialValue: this.translocoService.getActiveLang(),
  });

  readonly projectId = input<number | undefined>(undefined);
  readonly providerResults = input<ProviderOverallResult[]>([]);

  protected readonly isLoading = signal(false);
  private readonly requiredFunctionIds = signal<Set<number>>(new Set());
  private readonly moduleList = signal<Module[]>([]);
  private readonly expandedModuleIds = signal<Set<number>>(new Set());
  protected readonly selectedStatuses = signal<FunctionStatus[]>([]);
  protected readonly providerSupportFilters = signal<Map<string, SupportFilterValue[]>>(new Map());
  protected readonly statusOptions: {labelKey: string; value: FunctionStatus}[] = [
    {
      labelKey: 'project.steps.overallResults.moduleComparison.status.required',
      value: 'required',
    },
    {
      labelKey: 'project.steps.overallResults.moduleComparison.status.extra',
      value: 'extra',
    },
  ];
  protected readonly supportFilterOptions: {
    labelKey: string;
    value: SupportFilterValue;
    iconClass: string;
  }[] = [
    {
      labelKey: 'project.steps.overallResults.moduleComparison.legend.supported',
      value: 'supported',
      iconClass: 'pi pi-check',
    },
    {
      labelKey: 'project.steps.overallResults.moduleComparison.legend.notSupported',
      value: 'notSupported',
      iconClass: 'pi pi-times',
    },
  ];

  protected readonly orderedProviders = computed<ComparisonProvider[]>(() =>
    this.providerResults().map((providerResult) => ({
      hrToolDirectusId: providerResult.hrTool.id.toString(),
      translatedName: providerResult.translatedName,
      supportedFunctionIds: new Set(providerResult.hrTool.moduleFunctions.map((item) => item.id)),
    })),
  );

  protected readonly moduleRows = computed<ComparisonModuleRow[]>(() => {
    const modules = this.moduleList();
    const providers = this.orderedProviders();
    const requiredIds = this.requiredFunctionIds();
    const activeLanguage = this.activeLanguage();

    if (modules.length === 0 || providers.length === 0) {
      return [];
    }

    const supportedByAnyTool = new Set<number>();
    providers.forEach((provider) => {
      provider.supportedFunctionIds.forEach((functionId) => supportedByAnyTool.add(functionId));
    });

    const displayedFunctionIds = new Set<number>();
    requiredIds.forEach((functionId) => displayedFunctionIds.add(functionId));
    supportedByAnyTool.forEach((functionId) => {
      if (!requiredIds.has(functionId)) {
        displayedFunctionIds.add(functionId);
      }
    });

    return modules
      .map((module) => {
        const moduleFunctions = (module.functions ?? [])
          .filter((fn) => displayedFunctionIds.has(fn.id))
          .map((fn) => {
            const supportedByToolIds = new Set<string>();
            providers.forEach((provider) => {
              if (provider.supportedFunctionIds.has(fn.id)) {
                supportedByToolIds.add(provider.hrToolDirectusId);
              }
            });
            return {
              functionId: fn.id,
              functionName: getDirectusTranslation(fn.translations, activeLanguage, fn.name),
              status: requiredIds.has(fn.id) ? 'required' : 'extra',
              supportedByToolIds,
            } as ComparisonFunctionRow;
          });

        return {
          moduleId: module.id,
          moduleName: getDirectusTranslation(module.translations, activeLanguage, module.name),
          functions: moduleFunctions,
        } as ComparisonModuleRow;
      })
      .filter((module) => module.functions.length > 0);
  });

  protected readonly totalFunctionCount = computed(() =>
    this.moduleRows().reduce((sum, module) => sum + module.functions.length, 0),
  );

  protected readonly filteredModuleRows = computed<ComparisonModuleRow[]>(() => {
    const selected = this.selectedStatuses();
    const providerFilters = this.providerSupportFilters();
    const providers = this.orderedProviders();
    const selectedSet = new Set<FunctionStatus>(selected);

    if (selectedSet.size === 0) {
      return this.moduleRows().map((module) => ({
        ...module,
        functions: [],
      }));
    }

    return this.moduleRows().map((module) => ({
      ...module,
      functions: module.functions.filter((functionRow) => {
        if (!selectedSet.has(functionRow.status)) {
          return false;
        }

        return providers.every((provider) => {
          const allowedValues =
            providerFilters.get(provider.hrToolDirectusId) ?? ALL_SUPPORT_FILTER_VALUES;

          if (allowedValues.length === 0) {
            return false;
          }

          const supportValue: SupportFilterValue = functionRow.supportedByToolIds.has(
            provider.hrToolDirectusId,
          )
            ? 'supported'
            : 'notSupported';
          return allowedValues.includes(supportValue);
        });
      }),
    }));
  });

  protected readonly hasData = computed(
    () => this.orderedProviders().length > 0 && this.totalFunctionCount() > 0,
  );
  protected readonly hasVisibleModules = computed(() => this.filteredModuleRows().length > 0);
  protected readonly areAllVisibleModulesExpanded = computed(() => {
    const modules = this.filteredModuleRows();
    if (modules.length === 0) return false;
    const expanded = this.expandedModuleIds();
    return modules.every((module) => expanded.has(module.moduleId));
  });

  protected readonly requiredFunctionCount = computed(() =>
    this.moduleRows().reduce(
      (sum, module) => sum + module.functions.filter((fn) => fn.status === 'required').length,
      0,
    ),
  );

  protected readonly summaryCards = computed(() => {
    const requiredCount = this.requiredFunctionCount();
    const functions = this.moduleRows().flatMap((module) => module.functions);
    const extraFunctions = functions.filter((fn) => fn.status === 'extra');

    return this.orderedProviders().map((provider) => {
      const requiredFulfilled = functions.filter(
        (fn) => fn.status === 'required' && fn.supportedByToolIds.has(provider.hrToolDirectusId),
      ).length;
      return {
        hrToolDirectusId: provider.hrToolDirectusId,
        translatedName: provider.translatedName,
        requiredFulfilled,
        requiredMissing: Math.max(requiredCount - requiredFulfilled, 0),
        extraSupported: extraFunctions.filter((fn) =>
          fn.supportedByToolIds.has(provider.hrToolDirectusId),
        ).length,
      };
    });
  });

  constructor() {
    this.selectedStatuses.set([...ALL_FUNCTION_STATUSES]);

    effect(() => {
      const id = this.projectId();
      if (id === undefined) {
        this.requiredFunctionIds.set(new Set());
        this.moduleList.set([]);
        return;
      }
      void this.loadData(id);
    });
  }

  protected toggleModule(moduleId: number): void {
    const next = new Set(this.expandedModuleIds());
    if (next.has(moduleId)) {
      next.delete(moduleId);
    } else {
      next.add(moduleId);
    }
    this.expandedModuleIds.set(next);
  }

  protected isModuleExpanded(moduleId: number): boolean {
    return this.expandedModuleIds().has(moduleId);
  }

  protected isFunctionSupported(toolId: string, functionRow: ComparisonFunctionRow): boolean {
    return functionRow.supportedByToolIds.has(toolId);
  }

  protected expandAll(): void {
    const visibleModuleIds = this.filteredModuleRows().map((module) => module.moduleId);
    this.expandedModuleIds.set(new Set(visibleModuleIds));
  }

  protected collapseAll(): void {
    this.expandedModuleIds.set(new Set());
  }

  protected getProviderSupportFilterValues(providerId: string): SupportFilterValue[] {
    return this.providerSupportFilters().get(providerId) ?? ALL_SUPPORT_FILTER_VALUES;
  }

  protected onProviderSupportFilterChange(
    providerId: string,
    values: SupportFilterValue[] | undefined,
  ): void {
    const next = new Map(this.providerSupportFilters());
    next.set(providerId, values ?? []);
    this.providerSupportFilters.set(next);
  }

  private async loadData(projectId: number): Promise<void> {
    this.isLoading.set(true);
    try {
      const [coverageData, moduleMap] = await Promise.all([
        firstValueFrom(this.coverageDataService.getDataForCalculation({projectId})),
        firstValueFrom(this.directusService.loadModuleFunctionsGroupedByModule()),
      ]);

      const requiredIds = new Set(
        (coverageData.officeFunctionsDirectusIds ?? [])
          .map((id) => Number.parseInt(id, 10))
          .filter((id) => !Number.isNaN(id)),
      );

      this.requiredFunctionIds.set(requiredIds);
      this.moduleList.set(Array.from(moduleMap.values()));
      this.expandedModuleIds.set(new Set());
    } catch (error) {
      console.error('Error loading module comparison data:', error);
      this.requiredFunctionIds.set(new Set());
      this.moduleList.set([]);
      this.expandedModuleIds.set(new Set());
    } finally {
      this.isLoading.set(false);
    }
  }
}
